import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Checkbox, Chip, Icon, Pagination, Select, Snackbar, Spinner, TextField, Typography } from '@glplus/glpweb-dsl';
import { INBOX_CATEGORIES, runnerApi, type InboxCategory, type InboxCounts, type InboxMessage, type InboxSort } from '../lib/runnerApiClient';
import { emitInboxUpdated, routeForScreen } from '../lib/inbox';
import { MUTED_TEXT, ORANGE, ORANGE_LINK_STYLE } from '../styles/colors';

export const PAGE_SIZE = 50;
const SORTS: InboxSort[] = ['unread_first', 'alerts_first', 'date_desc', 'date_asc'];
const RED = '#EA4B48';
const NAVY = '#343F5B';
const BORDER = '#e3e8ee';
const TEXT = '#1f2937';

type Snack = { open: boolean; msg: string; variant: 'success' | 'error' | 'undo'; actionLabel?: string; onAction?: () => void };
const CLOSED: Snack = { open: false, msg: '', variant: 'success' };

function formatDate(iso: string, lang: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(lang.startsWith('es') ? 'es-US' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(iso: string, lang: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(lang.startsWith('es') ? 'es-US' : 'en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function Messages() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const alertsOnly = params.get('alerts') === '1';

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [exact, setExact] = useState(false);
  const [sort, setSort] = useState<InboxSort>('unread_first');
  const [category, setCategory] = useState<InboxCategory | ''>('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<InboxMessage[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<InboxCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [snack, setSnack] = useState<Snack>(CLOSED);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(id);
  }, [search]);

  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [debouncedSearch, exact, sort, category, alertsOnly]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, c] = await Promise.all([
        runnerApi.messages({
          sort,
          search: debouncedSearch || undefined,
          exact,
          category: category || undefined,
          alert: alertsOnly || undefined,
          skip: (page - 1) * PAGE_SIZE,
          take: PAGE_SIZE,
        }),
        runnerApi.messageCounts(),
      ]);
      setItems(alertsOnly ? list.messages.filter((m) => m.alert) : list.messages);
      setTotal(list.total);
      setCounts(c);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('messages.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [sort, debouncedSearch, exact, category, alertsOnly, page, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const afterChange = useCallback(() => {
    emitInboxUpdated();
    void load();
  }, [load]);

  const undoDelete = useCallback((ids: string[]) => {
    void (async () => {
      try {
        await Promise.all(ids.map((id) => runnerApi.restoreMessage(id)));
        afterChange();
      } catch {
        setSnack({ open: true, variant: 'error', msg: t('messages.restoreFailed') });
      }
    })();
  }, [afterChange, t]);

  const showDeleted = useCallback((ids: string[]) => {
    setSnack({
      open: true,
      variant: 'undo',
      msg: t('messages.deleted', { count: ids.length }),
      actionLabel: t('messages.undo'),
      onAction: () => undoDelete(ids),
    });
  }, [t, undoDelete]);

  useEffect(() => {
    const deletedId = (location.state as { deletedId?: string } | null)?.deletedId;
    if (deletedId) {
      showDeleted([deletedId]);
      void navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
  }, [location.state, location.pathname, location.search, navigate, showDeleted]);

  const onDelete = async (m: InboxMessage) => {
    setItems((prev) => prev.filter((x) => x.id !== m.id));
    try {
      await runnerApi.deleteMessage(m.id);
      showDeleted([m.id]);
    } catch {
      setSnack({ open: true, variant: 'error', msg: t('messages.deleteFailed') });
    }
    afterChange();
  };

  const onMarkAllRead = async () => {
    try {
      const r = await runnerApi.readAllMessages();
      setSnack({ open: true, variant: 'success', msg: t('messages.markedRead', { count: r.updated }) });
    } catch {
      setSnack({ open: true, variant: 'error', msg: t('messages.updateFailed') });
    }
    afterChange();
  };

  const onBulk = async (action: 'read' | 'unread' | 'delete') => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (action === 'delete') setItems((prev) => prev.filter((x) => !selected.has(x.id)));
    setSelected(new Set());
    try {
      const r = await runnerApi.bulkMessages(ids, action);
      if (action === 'delete') showDeleted(ids);
      else setSnack({ open: true, variant: 'success', msg: t(action === 'read' ? 'messages.markedRead' : 'messages.markedUnread', { count: r.updated }) });
    } catch {
      setSnack({ open: true, variant: 'error', msg: t('messages.updateFailed') });
    }
    afterChange();
  };

  const toggle = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const allSelected = items.length > 0 && items.every((m) => selected.has(m.id));
  const someSelected = !allSelected && items.some((m) => selected.has(m.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map((m) => m.id)));

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const sortOptions = useMemo(() => SORTS.map((s) => ({ value: s, label: t(`messages.sort.${s}`) })), [t]);
  const categoryOptions = useMemo(() => [
    { value: '', label: t('messages.category.all') },
    ...INBOX_CATEGORIES.map((c) => ({
      value: c,
      label: counts ? `${t(`messages.category.${c}`)} (${counts.categoryTotals[c]?.total ?? 0})` : t(`messages.category.${c}`),
    })),
  ], [t, counts]);

  return (
    <Box data-testid="page-messages">
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 800, color: TEXT, fontSize: { xs: '24px', md: '30px' } }}>
          {alertsOnly ? t('messages.alertsTitle') : t('messages.title')}
        </Typography>
        {alertsOnly && (
          <Link to="/messages" data-testid="messages-show-all" style={ORANGE_LINK_STYLE}>{t('messages.showAll')}</Link>
        )}
      </Box>

      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 320px' }, alignItems: 'start' }}>
        <Box sx={{ minWidth: 0, order: { xs: 2, lg: 1 } }}>
          {items.length > 0 && (
            <Box data-testid="messages-toolbar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1, px: 1 }}>
              <Checkbox
                ariaLabel={t('messages.selectAll')}
                checked={allSelected}
                indeterminate={someSelected}
                onChange={toggleAll}
                label={selected.size > 0 ? t('messages.selectedCount', { count: selected.size }) : t('messages.selectAll')}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {selected.size > 0 ? (
                  <>
                    <Button variant="text" ariaLabel={t('messages.bulkRead')} buttonText={t('messages.bulkRead')} textColor={NAVY} dataTestId="messages-bulk-read" onClick={() => void onBulk('read')} sx={{ textTransform: 'none', fontWeight: 700 }} />
                    <Button variant="text" ariaLabel={t('messages.bulkUnread')} buttonText={t('messages.bulkUnread')} textColor={NAVY} dataTestId="messages-bulk-unread" onClick={() => void onBulk('unread')} sx={{ textTransform: 'none', fontWeight: 700 }} />
                    <Button variant="text" ariaLabel={t('messages.bulkDelete')} buttonText={t('messages.bulkDelete')} textColor={RED} dataTestId="messages-bulk-delete" onClick={() => void onBulk('delete')} sx={{ textTransform: 'none', fontWeight: 700 }} />
                  </>
                ) : (
                  (counts?.unreadTotal ?? 0) > 0 && (
                    <Button variant="text" ariaLabel={t('messages.markAllRead')} buttonText={t('messages.markAllRead')} textColor={NAVY} dataTestId="messages-mark-all-read" onClick={() => void onMarkAllRead()} sx={{ textTransform: 'none', fontWeight: 700 }} />
                  )
                )}
              </Box>
            </Box>
          )}

          {error ? (
            <Alert severity="error" data-testid="messages-error">{error}</Alert>
          ) : loading && items.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <Spinner aria-label={t('messages.loading')} />
            </Box>
          ) : items.length === 0 ? (
            <Box data-testid="messages-empty" sx={{ backgroundColor: '#fff', borderRadius: 2.5, p: 4, textAlign: 'center' }}>
              <Icon name="inbox" size={40} color={MUTED_TEXT} ariaLabel="" />
              <Typography component="h2" sx={{ fontWeight: 800, fontSize: '17px', color: TEXT, mt: 1 }}>
                {debouncedSearch || category ? t('messages.emptyFilteredTitle') : alertsOnly ? t('messages.emptyAlertsTitle') : t('messages.emptyTitle')}
              </Typography>
              <Typography sx={{ color: MUTED_TEXT, fontSize: '14px', mt: 0.5 }}>{t('messages.emptyBody')}</Typography>
            </Box>
          ) : (
            <Box component="ul" data-testid="messages-list" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {items.map((m) => (
                <MessageRow
                  key={m.id}
                  message={m}
                  selected={selected.has(m.id)}
                  lang={i18n.language}
                  onToggle={() => toggle(m.id)}
                  onOpen={() => void navigate(`/messages/${m.id}`)}
                  onDelete={() => void onDelete(m)}
                />
              ))}
            </Box>
          )}

          {totalPages > 1 && (
            <Box sx={{ mt: 2 }}>
              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                align="center"
                ariaLabel={t('messages.pagination')}
                pageAriaLabel={t('messages.goToPage')}
                prevAriaLabel={t('messages.prevPage')}
                nextAriaLabel={t('messages.nextPage')}
                activeBgColor={NAVY}
                activeColor="#fff"
              />
            </Box>
          )}
        </Box>

        <Box component="aside" aria-label={t('messages.filtersLabel')} sx={{ order: { xs: 1, lg: 2 }, backgroundColor: '#fff', borderRadius: 2.5, borderTop: `3px solid ${NAVY}`, p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box role="search">
            <TextField
              id="messages-search"
              label={t('messages.search')}
              placeholder={t('messages.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              size="small"
              dataTestId="messages-search"
            />
          </Box>
          <Checkbox ariaLabel={t('messages.exact')} label={t('messages.exact')} checked={exact} onChange={(e) => setExact(e.target.checked)} />
          <Select
            id="messages-sort"
            ariaLabel={t('messages.sortLabel')}
            label={t('messages.sortLabel')}
            options={sortOptions}
            value={sort}
            onChange={(_e, v) => setSort(v as InboxSort)}
            size="small"
            fullWidth
          />
          <Select
            id="messages-category"
            ariaLabel={t('messages.categoryLabel')}
            label={t('messages.categoryLabel')}
            options={categoryOptions}
            value={category}
            onChange={(_e, v) => setCategory(v as InboxCategory | '')}
            size="small"
            fullWidth
          />
        </Box>
      </Box>

      <Snackbar
        open={snack.open}
        message={snack.msg}
        variant={snack.variant}
        actionLabel={snack.actionLabel}
        onAction={() => {
          snack.onAction?.();
          setSnack(CLOSED);
        }}
        onClose={() => setSnack(CLOSED)}
      />
    </Box>
  );
}

function MessageRow({ message, selected, lang, onToggle, onOpen, onDelete }: { message: InboxMessage; selected: boolean; lang: string; onToggle: () => void; onOpen: () => void; onDelete: () => void }) {
  const { t } = useTranslation();
  const unread = !message.isRead;
  const title = message.title ?? t('messages.untitled');
  return (
    <Box component="li" data-testid={`message-row-${message.id}`} data-unread={unread ? 'true' : 'false'} data-alert={message.alert ? 'true' : 'false'} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        aria-hidden="true"
        data-testid={unread ? 'message-unread-dot' : undefined}
        sx={{ width: 10, height: 10, flexShrink: 0, borderRadius: '50%', backgroundColor: unread ? (message.alert ? RED : ORANGE) : 'transparent' }}
      />
      <Box sx={{ display: 'flex', flex: 1, minWidth: 0, alignItems: 'stretch', backgroundColor: '#fff', border: `1px solid ${selected ? NAVY : BORDER}`, borderRadius: '8px', overflow: 'hidden' }}>
        <Box sx={{ width: 4, flexShrink: 0, backgroundColor: message.alert ? RED : BORDER }} />
        <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5 }}>
          <Checkbox ariaLabel={t('messages.selectOne', { title })} checked={selected} onChange={onToggle} />
        </Box>
        <Box
          role="button"
          tabIndex={0}
          aria-label={unread ? t('messages.openUnread', { title }) : t('messages.open', { title })}
          onClick={onOpen}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen();
            }
          }}
          sx={{ flex: 1, minWidth: 0, py: 1.25, pr: 1, cursor: 'pointer', '&:hover': { backgroundColor: '#fafbfc' }, '&:focus-visible': { outline: `2px solid ${NAVY}`, outlineOffset: -2 } }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {message.alert && <Icon name="warningAmber" size={18} color={RED} ariaLabel="" />}
            <Typography sx={{ flex: 1, minWidth: 0, fontWeight: unread ? 800 : 500, color: unread ? TEXT : MUTED_TEXT, fontSize: '15px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</Typography>
            <Typography sx={{ color: MUTED_TEXT, fontSize: '12px', flexShrink: 0 }}>{formatDate(message.createdAt, lang)}</Typography>
          </Box>
          {message.body && (
            <Typography sx={{ color: unread ? '#374151' : MUTED_TEXT, fontSize: '13px', mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{message.body}</Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', pr: 0.5 }}>
          <Button variant="iconButton" ariaLabel={t('messages.deleteOne', { title })} startIcon="delete" dataTestId={`message-delete-${message.id}`} onClick={onDelete} sx={{ color: MUTED_TEXT, '& svg': { color: MUTED_TEXT } }} />
        </Box>
      </Box>
    </Box>
  );
}

export function MessageDetail() {
  const { t, i18n } = useTranslation();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [message, setMessage] = useState<InboxMessage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setMessage(null);
    setError(null);
    runnerApi.message(id)
      .then((r) => {
        if (cancelled) return;
        setMessage(r.message);
        emitInboxUpdated();
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : t('messages.loadFailed'));
      });
    return () => { cancelled = true; };
  }, [id, t]);

  const onDelete = async () => {
    setBusy(true);
    try {
      await runnerApi.deleteMessage(id);
      emitInboxUpdated();
      void navigate('/messages', { state: { deletedId: id } });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('messages.deleteFailed'));
      setBusy(false);
    }
  };

  const onMarkUnread = async () => {
    setBusy(true);
    try {
      await runnerApi.setMessageRead(id, false);
      emitInboxUpdated();
      void navigate('/messages');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('messages.updateFailed'));
      setBusy(false);
    }
  };

  const target = routeForScreen(message?.payload?.screen, message?.payload?.runId);

  return (
    <Box data-testid="page-message-detail" sx={{ maxWidth: 760 }}>
      <Box sx={{ mb: 2 }}>
        <Link to="/messages" data-testid="message-back" style={ORANGE_LINK_STYLE}>← {t('messages.back')}</Link>
      </Box>
      {error ? (
        <Alert severity="error" data-testid="message-error">{error}</Alert>
      ) : !message ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Spinner aria-label={t('messages.loading')} />
        </Box>
      ) : (
        <Box sx={{ backgroundColor: '#fff', borderRadius: 2.5, borderTop: `3px solid ${message.alert ? RED : NAVY}`, p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            <Chip label={t(`messages.category.${message.category}`)} size="small" variant="soft" color={message.alert ? 'error' : 'muted'} />
            {message.alert && <Chip label={t('messages.alertChip')} size="small" variant="soft" color="error" />}
            <Typography sx={{ color: MUTED_TEXT, fontSize: '13px' }}>{formatDateTime(message.createdAt, i18n.language)}</Typography>
          </Box>
          <Typography variant="h4" component="h1" data-testid="message-title" sx={{ fontWeight: 800, color: TEXT, fontSize: { xs: '22px', md: '26px' }, mb: 1.5 }}>
            {message.title ?? t('messages.untitled')}
          </Typography>
          {message.body && (
            <Typography data-testid="message-body" sx={{ color: '#374151', fontSize: '15px', whiteSpace: 'pre-wrap' }}>{message.body}</Typography>
          )}
          {target && (
            <Box sx={{ mt: 2 }}>
              <Link to={target.to} data-testid="message-open-target" style={ORANGE_LINK_STYLE}>{t('messages.goTo', { page: t(target.navKey) })}</Link>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1.5, mt: 3, flexWrap: 'wrap' }}>
            <Button variant="outlined" ariaLabel={t('messages.markUnread')} buttonText={t('messages.markUnread')} dataTestId="message-mark-unread" disabled={busy} onClick={() => void onMarkUnread()} textColor={NAVY} borderColor={NAVY} sx={{ backgroundColor: '#fff', textTransform: 'none', fontWeight: 700 }} />
            <Button variant="outlined" ariaLabel={t('messages.delete')} buttonText={t('messages.delete')} dataTestId="message-delete" disabled={busy} onClick={() => void onDelete()} textColor={RED} borderColor={RED} sx={{ backgroundColor: '#fff', textTransform: 'none', fontWeight: 700 }} />
          </Box>
        </Box>
      )}
    </Box>
  );
}
